import { ApiResponse } from 'apisauce';
import { locals } from 'src/constants';

/**
 * @param {(Error|string|undefined)} error
 * @return {string}
 */
export const getErrorMessage = (error: Error) => {
  if (typeof error === 'string') {
    return error;
  }

  return locals.errors.unexpectedError;
};

/**
 * @param {ApisauceResponse} response
 * @return {string}
 */
export const getApiErrorMessage = (response: ApiResponse<any>) => {
  const ApisauceProblemCodes = {
    CLIENT_ERROR: 'CLIENT_ERROR',
    SERVER_ERROR: 'SERVER_ERROR',
    TIMEOUT_ERROR: 'TIMEOUT_ERROR',
    CONNECTION_ERROR: 'CONNECTION_ERROR',
    NETWORK_ERROR: 'NETWORK_ERROR',
    CANCEL_ERROR: 'CANCEL_ERROR',
  };

  switch (response.problem) {
    case ApisauceProblemCodes.CLIENT_ERROR:
      return getErrorMessage(response.data?.detail);
    case ApisauceProblemCodes.SERVER_ERROR:
      return locals.errors.server;
    case ApisauceProblemCodes.TIMEOUT_ERROR:
      return locals.errors.timeout;
    case ApisauceProblemCodes.CONNECTION_ERROR:
      return locals.errors.connection;
    case ApisauceProblemCodes.NETWORK_ERROR:
      return locals.errors.network;
    case ApisauceProblemCodes.CANCEL_ERROR:
      return locals.errors.cancelRequest;
    default:
      return '';
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null
);

const normalizeErrorKey = (key: string) => key.toLowerCase().replace(/[^a-z]/g, '');

const findErrorValue = (
  value: unknown,
  searchedKeys: string[],
  visited = new Set<unknown>(),
): string | undefined => {
  if (!isRecord(value) || visited.has(value)) {
    return undefined;
  }

  visited.add(value);
  const entries = Object.entries(value);
  for (const searchedKey of searchedKeys) {
    const foundEntry = entries.find(([key]) => normalizeErrorKey(key) === searchedKey);
    if (typeof foundEntry?.[1] === 'string' && foundEntry[1].trim()) {
      return foundEntry[1].trim();
    }
  }

  for (const nestedValue of Object.values(value)) {
    const foundValue = findErrorValue(nestedValue, searchedKeys, visited);
    if (foundValue) {
      return foundValue;
    }
  }

  return undefined;
};

const serializeErrorResponse = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  try {
    const serializedValue = JSON.stringify(value);
    return serializedValue && serializedValue !== '{}' ? serializedValue.slice(0, 1000) : undefined;
  } catch (error) {
    return undefined;
  }
};

/**
 * Extracts a useful message from an endpoint not controlled by Flatshow.
 * Supports Axios errors, successful HTTP responses containing an error payload,
 * plain strings and unknown JSON structures.
 */
export const getExternalApiErrorMessage = (error: unknown): string => {
  const response = isRecord(error) && isRecord(error.response) ? error.response : undefined;
  const responseData = response?.data;
  const errorData = responseData !== undefined ? responseData : error;

  if (typeof errorData === 'string' && errorData.trim()) {
    return errorData.trim();
  }

  const errorName = findErrorValue(errorData, ['error', 'code']);
  const errorDescription = findErrorValue(errorData, ['errordescription', 'description', 'detail', 'message']);
  const errorParts = Array.from(new Set([errorName, errorDescription].filter(Boolean)));
  if (errorParts.length) {
    return errorParts.join(': ');
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  const statusText = response && typeof response.statusText === 'string' ? response.statusText.trim() : '';
  if (statusText) {
    return statusText;
  }

  return serializeErrorResponse(errorData) || locals.errors.unexpectedError;
};
